import React, { useMemo, useEffect, useState } from 'react';
import { Box, Paper, Typography, Grid, useTheme, Tooltip } from '@mui/material';
import {
    AlertCircle,
    ClipboardList,
    Clock,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Navigation,
    Send,
    FileText,
    Eye,
    Smartphone,
    Monitor,
    TrendingUp,
    TrendingDown,
    Users,
    UserPlus,
    MapPin,
    Globe,
    Calculator,
    Target,
    Zap,
    Briefcase,
    Home,
    Award,
    Calendar,
    DollarSign
} from 'lucide-react';
import { parseISO, differenceInDays } from 'date-fns';
import { determineQuoteStatus } from './StatusMapping';
import appleLogoSrc from '../assets/apple_logo.png';
import androidLogoSrc from '../assets/android_logo.png';
import windowsLogoSrc from '../assets/windows_logo.png';
import CompactDateRangePicker from './CompactDateRangePicker';

const KpiCard = ({ title, value, icon, color, subtitle, change, onClick, isActive = false, background, tooltip }) => {
    const theme = useTheme();

    const cardContent = (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                p: 0,
                height: 200,
                minHeight: 200,
                backgroundColor: isActive
                    ? theme.palette.primary.dark
                    : background === 'transparent'
                        ? 'rgba(30, 30, 30, 0.7) !important'
                        : theme.palette.background.paper,
                backdropFilter: background === 'transparent' ? 'blur(5px)' : 'none',
                borderRadius: 2,
                border: '2px solid',
                borderColor: isActive ? theme.palette.primary.main : 'divider',
                display: 'flex',
                flexDirection: 'column',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease-in-out',
                overflow: 'visible',
                '&:hover': onClick ? {
                    backgroundColor: isActive
                        ? theme.palette.primary.dark
                        : background === 'transparent'
                            ? 'rgba(42, 42, 42, 0.3)'
                            : '#2a2a2a',
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                } : {}
            }}
        >
            {/* ...existing code... */}
            <Box
                sx={{
                    flex: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center'
                }}
            >
                {/* Icon at the top */}
                <Box
                    sx={{
                        borderRadius: '50%',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette[color]?.main || '#555',
                        color: theme.palette[color]?.contrastText || '#fff',
                        mb: 1.5
                    }}
                >
                    {icon}
                </Box>

                {/* Title */}
                <Typography
                    variant="body2"
                    color={isActive ? "primary.contrastText" : "text.secondary"}
                    sx={{ mb: 0.5, fontWeight: 'medium', fontSize: '0.75rem' }}
                >
                    {title}
                </Typography>

                {/* Value */}
                <Typography
                    variant="h4"
                    component="div"
                    sx={{
                        fontWeight: 'bold',
                        mb: 0.5,
                        fontSize: '1.5rem',
                        color: isActive ? theme.palette.primary.contrastText : 'inherit'
                    }}
                >
                    {value}
                </Typography>

                {/* Subtitle */}
                {subtitle && (
                    <Typography
                        variant="caption"
                        color={isActive ? "primary.contrastText" : "text.secondary"}
                        sx={{ fontSize: '0.7rem' }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>

            {/* Footer with change indicator */}
            {change !== undefined && (
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: isActive ? 'rgba(255,255,255,0.1)' : 'divider',
                        px: 2,
                        py: 1,
                        backgroundColor: isActive
                            ? 'rgba(0,0,0,0.1)'
                            : background === 'transparent'
                                ? 'rgba(0,0,0,0.3)'
                                : 'rgba(0,0,0,0.05)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {change > 0 ? (
                            <TrendingUp size={16} style={{ color: title === "Drop-offs" ? '#f44336' : '#4caf50', marginRight: 4 }} />
                        ) : change < 0 ? (
                            <TrendingDown size={16} style={{ color: title === "Drop-offs" ? '#4caf50' : '#f44336', marginRight: 4 }} />
                        ) : null}
                        <Typography
                            variant="caption"
                            sx={{
                                color: title === "Drop-offs"
                                    ? (change > 0 ? '#f44336' : change < 0 ? '#4caf50' : 'text.secondary')
                                    : (change > 0 ? '#4caf50' : change < 0 ? '#f44336' : 'text.secondary'),
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {change > 0 ? '+' : ''}{change}%
                        </Typography>
                    </Box>
                </Box>
            )}
        </Paper>
    );

    return tooltip ? (
        <Tooltip
            title={tooltip}
            arrow
            placement="bottom"
            sx={{
                '& .MuiTooltip-tooltip': {
                    backgroundColor: '#333',
                    color: '#fff',
                    fontSize: '0.75rem',
                    maxWidth: 250,
                    textAlign: 'center'
                }
            }}
        >
            {cardContent}
        </Tooltip>
    ) : cardContent;
};

const KpiCardWithBreakdown = ({ title, value, icon, color, breakdown, change, onClick, isActive = false, background, showFooter = false, height = 200, tooltip }) => {
    const theme = useTheme();

    const cardContent = (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                p: 0,
                height: height,
                minHeight: height,
                backgroundColor: isActive
                    ? theme.palette.primary.dark
                    : background === 'transparent'
                        ? 'rgba(30, 30, 30, 0.7) !important'
                        : theme.palette.background.paper,
                backdropFilter: background === 'transparent' ? 'blur(5px)' : 'none',
                borderRadius: 2,
                border: '2px solid',
                borderColor: isActive ? theme.palette.primary.main : 'divider',
                display: 'flex',
                flexDirection: 'column',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease-in-out',
                overflow: 'hidden',
                '&:hover': onClick ? {
                    backgroundColor: isActive
                        ? theme.palette.primary.dark
                        : background === 'transparent'
                            ? 'rgba(42, 42, 42, 0.3)'
                            : '#2a2a2a',
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                } : {}
            }}
        >
            {/* ...existing code... */}
            <Box
                sx={{
                    flex: 1,
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    minHeight: 0,
                    overflow: 'hidden'
                }}
            >
                {/* Icon at the top */}
                <Box
                    sx={{
                        borderRadius: '50%',
                        width: 48,
                        height: 48,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette[color]?.dark || '#555',
                        color: theme.palette[color]?.contrastText || '#fff',
                        mb: 1.5
                    }}
                >
                    {icon}
                </Box>

                {/* Title */}
                <Typography
                    variant="body2"
                    color={isActive ? "primary.contrastText" : "text.secondary"}
                    sx={{ mb: 0.5, fontWeight: 'medium', fontSize: '0.75rem' }}
                >
                    {title}
                </Typography>

                {/* Value */}
                <Typography
                    variant="h4"
                    component="div"
                    sx={{
                        fontWeight: 'bold',
                        mb: 0.5,
                        fontSize: '1.5rem',
                        color: isActive ? theme.palette.primary.contrastText : 'inherit'
                    }}
                >
                    {value}
                </Typography>

                {/* Change indicator - only shown if not using footer for breakdown */}
                {change !== undefined && !showFooter && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {change > 0 ? (
                            <TrendingUp size={16} style={{ color: title === "Drop-offs" ? '#f44336' : '#4caf50', marginRight: 3 }} />
                        ) : change < 0 ? (
                            <TrendingDown size={16} style={{ color: title === "Drop-offs" ? '#4caf50' : '#f44336', marginRight: 3 }} />
                        ) : null}
                        <Typography
                            variant="caption"
                            sx={{
                                color: title === "Drop-offs"
                                    ? (change > 0 ? '#f44336' : change < 0 ? '#4caf50' : 'text.secondary')
                                    : (change > 0 ? '#4caf50' : change < 0 ? '#f44336' : 'text.secondary'),
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {change > 0 ? '+' : ''}{change}%
                        </Typography>
                    </Box>
                )}

                {/* Breakdown in main card (for footer layout) - simplified version */}
                {breakdown && showFooter && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        {breakdown.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.3,
                                }}
                            >
                                {/* Handle both image sources and React elements */}
                                {typeof item.icon === 'string' ? (
                                    <img
                                        src={item.icon}
                                        alt=""
                                        style={{ width: '12px', height: '14px' }}
                                    />
                                ) : (
                                    <Box sx={{ color: isActive ? theme.palette.primary.contrastText : 'text.secondary' }}>
                                        {item.icon}
                                    </Box>
                                )}
                                {item.label && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.6rem',
                                            color: isActive ? theme.palette.primary.contrastText : 'text.secondary',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {item.label}
                                    </Typography>
                                )}
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        color: isActive ? theme.palette.primary.contrastText : 'text.secondary'
                                    }}
                                >
                                    {item.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Inline Breakdown (for non-footer layout) */}
                {breakdown && !showFooter && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {breakdown.map((item, index) => (
                            <Typography
                                key={index}
                                variant="caption"
                                sx={{
                                    fontSize: '0.6rem',
                                    color: isActive ? theme.palette.primary.contrastText : 'text.secondary',
                                    px: 0.5,
                                    py: 0.25,
                                    bgcolor: isActive ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    borderRadius: 0.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '2px'
                                }}
                            >
                                {/* Handle both image sources and React elements */}
                                {typeof item.icon === 'string' ? (
                                    <img
                                        src={item.icon}
                                        alt=""
                                        style={{ width: '16px', height: '16px', marginRight: '4px' }}
                                    />
                                ) : (
                                    <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                                        {item.icon}
                                    </Box>
                                )}
                                {item.label ? `${item.label}: ${item.value}` : item.value}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>

            {/* Footer with change indicator (for footer layout) */}
            {showFooter && change !== undefined && (
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: isActive ? 'rgba(255,255,255,0.1)' : 'divider',
                        px: 2,
                        py: 1,
                        backgroundColor: isActive
                            ? 'rgba(0,0,0,0.1)'
                            : background === 'transparent'
                                ? 'rgba(0,0,0,0.3)'
                                : 'rgba(0,0,0,0.05)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '40px',
                        maxHeight: '40px'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {change > 0 ? (
                            <TrendingUp size={16} style={{ color: title === "Drop-offs" ? '#f44336' : '#4caf50', marginRight: 4 }} />
                        ) : change < 0 ? (
                            <TrendingDown size={16} style={{ color: title === "Drop-offs" ? '#4caf50' : '#f44336', marginRight: 4 }} />
                        ) : null}
                        <Typography
                            variant="caption"
                            sx={{
                                color: title === "Drop-offs"
                                    ? (change > 0 ? '#f44336' : change < 0 ? '#4caf50' : 'text.secondary')
                                    : (change > 0 ? '#4caf50' : change < 0 ? '#f44336' : 'text.secondary'),
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                            }}
                        >
                            {change > 0 ? '+' : ''}{change}%
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Original Footer Breakdown (for non-footer layout) */}
            {breakdown && !showFooter && (
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: isActive ? 'rgba(255,255,255,0.1)' : 'divider',
                        px: 2,
                        py: 1,
                        backgroundColor: isActive
                            ? 'rgba(0,0,0,0.1)'
                            : background === 'transparent'
                                ? 'rgba(0,0,0,0.3)'
                                : 'rgba(0,0,0,0.05)'
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                        {breakdown.map((item, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                <img
                                    src={item.icon}
                                    alt=""
                                    style={{ width: '20px', height: '20px' }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        color: isActive ? theme.palette.primary.contrastText : 'text.primary'
                                    }}
                                >
                                    {item.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );

    return tooltip ? (
        <Tooltip
            title={tooltip}
            arrow
            placement="bottom"
            sx={{
                '& .MuiTooltip-tooltip': {
                    backgroundColor: '#333',
                    color: '#fff',
                    fontSize: '0.75rem',
                    maxWidth: 250,
                    textAlign: 'center'
                }
            }}
        >
            {cardContent}
        </Tooltip>
    ) : cardContent;
};

const QuoteKPIs = ({ quotes = [], logs = [], isLeadMode = false, dateRange = null, onFilterChange, onDateRangeChange }) => {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);

    // Handle filter click
    const handleFilterClick = (filterType) => {
        const newFilter = activeFilter === filterType ? null : filterType;
        setActiveFilter(newFilter);
        if (onFilterChange) {
            onFilterChange(newFilter);
        }
    };

    // Handle date range change
    const handleDateRangeChange = (range) => {
        if (onDateRangeChange) {
            onDateRangeChange(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'));
        }
    };

    // Fetch analytics data for lead mode
    useEffect(() => {
        const fetchAnalytics = async () => {
            if (!dateRange?.start || !dateRange?.end) return;

            setLoading(true);
            try {
                const response = await fetch(
                    `/api/retool-leads-analytics-fixed?start=${dateRange.start}&end=${dateRange.end}`
                );
                const data = await response.json();
                setAnalytics(data);
            } catch (error) {
                console.error('Failed to fetch analytics:', error);
            } finally {
                setLoading(false);
            }
        };

        if (isLeadMode && dateRange?.start && dateRange?.end) {
            fetchAnalytics();
        }
    }, [isLeadMode, dateRange]);

    const kpis = useMemo(() => {
        if (isLeadMode && analytics) {
            // Return lead analytics
            const current = analytics.current_period;
            const changes = analytics.changes;

            return {
                totalPageViews: current.page_views.total,
                totalLeads: current.leads.total,
                onsiteBookings: current.leads.onsite_bookings,
                onlineBookings: current.leads.online_bookings,
                priceEstimates: current.leads.price_estimates,
                priceEstimateConversions: current.leads.price_estimate_conversions,
                dropoffs: current.leads.dropoffs,
                completedBookings: current.leads.completed_bookings,
                iphoneViews: current.page_views.iphone,
                androidViews: current.page_views.android,
                mobileViews: current.page_views.mobile,
                pcViews: current.page_views.pc,
                dropoffsOnsite: current.leads.dropoffs_breakdown?.onsite || 0,
                dropoffsOnline: current.leads.dropoffs_breakdown?.online || 0,
                dropoffsEstimate: current.leads.dropoffs_breakdown?.estimate || 0,
                changes: changes
            };
        }

        // Original quote logic
        const quotesWithStatus = quotes.map(quote => {
            const quoteLogs = logs.filter(log => log.quote_id === quote.id);
            const status = determineQuoteStatus(quoteLogs);
            return { ...quote, status };
        });

        const totalQuotes = quotes.length;

        const countByStatus = quotesWithStatus.reduce((acc, quote) => {
            const status = quote.status || 'New Lead';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const needAttention =
            (countByStatus['Quote On-Site'] || 0) +
            (countByStatus['Quote Sent'] || 0);

        const overThreshold = quotesWithStatus.filter(quote => {
            const status = quote.status || 'New Lead';
            const quoteLogs = logs.filter(log => log.quote_id === quote.id);
            const sortedLogs = [...quoteLogs].sort((a, b) =>
                new Date(b.date_logged) - new Date(a.date_logged)
            );

            const lastLogDate = sortedLogs.length > 0
                ? sortedLogs[0].date_logged
                : quote.date_issued;

            if (!lastLogDate) return false;

            const daysInStatus = differenceInDays(new Date(), parseISO(lastLogDate));

            const thresholds = {
                'New Lead': 3,
                'Office Quote': 2,
                'Quote On-Site': 5,
                'Quote Travel to Site': 1,
                'Quote Sent': 0,
                'Quote Won': 2,
                'Job - Invoice Deposit': 5,
            };

            const threshold = thresholds[status] || 0;
            return daysInStatus > threshold && threshold > 0;
        }).length;

        let avgTimeToWin = 0;
        const wonQuotes = quotesWithStatus.filter(q => q.status === 'Quote Won');

        if (wonQuotes.length > 0) {
            let totalDays = 0;
            let validQuotes = 0;

            wonQuotes.forEach(quote => {
                const quoteLogs = logs.filter(log => log.quote_id === quote.id);

                if (quoteLogs.length >= 2) {
                    const creationLog = quoteLogs.find(log =>
                        log.message && log.message.includes('Created Quote')
                    );

                    const wonLog = quoteLogs.find(log =>
                        log.message && log.message.includes('Quote Won')
                    );

                    if (creationLog && wonLog) {
                        const creationDate = parseISO(creationLog.date_logged);
                        const wonDate = parseISO(wonLog.date_logged);
                        totalDays += differenceInDays(wonDate, creationDate);
                        validQuotes++;
                    }
                }
            });

            avgTimeToWin = validQuotes > 0 ? Math.round(totalDays / validQuotes) : 0;
        }

        return {
            totalQuotes,
            newLeads: countByStatus['New Lead'] || 0,
            onSite: countByStatus['Quote On-Site'] || 0,
            quotesSent: countByStatus['Quote Sent'] || 0,
            quotesWon: countByStatus['Quote Won'] || 0,
            jobsInProgress: countByStatus['Job - Invoice Deposit'] || 0,
            needAttention,
            overThreshold,
            avgTimeToWin
        };
    }, [quotes, logs, isLeadMode, analytics]);

    if (loading) {
        return (
            <Box sx={{ mb: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                <Typography variant="h6" color="text.secondary">Loading analytics...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 6, width: '100%' }}>
            <Grid container spacing={3} sx={{ width: '100%', mx: 0, justifyContent: 'flex-start' }}>
                {isLeadMode ? (
                    // Lead management KPIs
                    <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            {(() => {
                                const breakdownData = [
                                    { icon: appleLogoSrc, value: `${kpis.iphoneViews}` },
                                    { icon: androidLogoSrc, value: `${kpis.androidViews}` },
                                    { icon: windowsLogoSrc, value: `${kpis.pcViews}` }
                                ];
                                return (
                                    <KpiCardWithBreakdown
                                        title="Total Page Views"
                                        value={kpis.totalPageViews}
                                        icon={<Eye size={24} />}
                                        color="primary"
                                        background="transparent"
                                        change={kpis.changes?.page_views?.total}
                                        breakdown={breakdownData}
                                        onClick={() => handleFilterClick('page_views')}
                                        isActive={activeFilter === 'page_views'}
                                        showFooter={true}
                                        height={240}
                                        tooltip="Total number of page views on the Assist App, broken down by device type (iOS, Android, PC)"
                                    />
                                );
                            })()}
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Total Leads"
                                value={kpis.totalLeads}
                                icon={<Users size={24} />}
                                color="primary"
                                background="transparent"
                                change={kpis.changes?.leads?.total}
                                onClick={() => handleFilterClick('total_leads')}
                                isActive={activeFilter === 'total_leads'}
                                tooltip="Total number of Unique Customers that completed the form on the Assist App"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Onsite Bookings"
                                value={kpis.onsiteBookings}
                                icon={<Home size={24} />}
                                color="success"
                                background="transparent"
                                change={kpis.changes?.leads?.onsite_bookings}
                                onClick={() => handleFilterClick('onsite_bookings')}
                                isActive={activeFilter === 'onsite_bookings'}
                                tooltip="Number of customers who selected onsite consultations or appointments"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Online Bookings"
                                value={kpis.onlineBookings}
                                icon={<Globe size={24} />}
                                color="primary"
                                background="transparent"
                                change={kpis.changes?.leads?.online_bookings}
                                onClick={() => handleFilterClick('online_bookings')}
                                isActive={activeFilter === 'online_bookings'}
                                tooltip="Number of customers who selected Phone Consultations or Online Bookings"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Price Estimates"
                                value={kpis.priceEstimates}
                                icon={<Calculator size={24} />}
                                background="transparent"
                                color="warning"
                                change={kpis.changes?.leads?.price_estimates}
                                onClick={() => handleFilterClick('price_estimates')}
                                isActive={activeFilter === 'price_estimates'}
                                tooltip="Number of customers who requested price estimates through the Assist App"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Price Est - Conversions"
                                value={kpis.priceEstimateConversions}
                                icon={<Target size={24} />}
                                color="success"
                                background="transparent"
                                change={kpis.changes?.leads?.price_estimate_conversions}
                                onClick={() => handleFilterClick('price_estimate_conversions')}
                                isActive={activeFilter === 'price_estimate_conversions'}
                                tooltip="Number of price estimate requests that converted into actual bookings"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            {(() => {
                                const breakdownData = [
                                    { icon: <Home size={12} />, label: 'Onsite', value: `${kpis.dropoffsOnsite}` },
                                    { icon: <Globe size={12} />, label: 'Online', value: `${kpis.dropoffsOnline}` },
                                    { icon: <Calculator size={12} />, label: 'Estimate', value: `${kpis.dropoffsEstimate}` }
                                ];
                                return (
                                    <KpiCardWithBreakdown
                                        title="Drop-offs"
                                        value={kpis.dropoffs}
                                        icon={<XCircle size={24} />}
                                        color="error"
                                        background="transparent"
                                        change={kpis.changes?.leads?.dropoffs}
                                        breakdown={breakdownData}
                                        onClick={() => handleFilterClick('dropoffs')}
                                        isActive={activeFilter === 'dropoffs'}
                                        showFooter={true}
                                        height={240}
                                        tooltip="Number of incomplete lead processes where customers started but didn't finish their inquiry, broken down by request type (Onsite, Online, Price Estimate)"
                                    />
                                );
                            })()}
                        </Grid>

                        {/* Date Range Picker */}
                        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                            <Tooltip
                                title="Select the date range for analytics data. All metrics will be calculated based on this period."
                                arrow
                                placement="bottom"
                                sx={{
                                    '& .MuiTooltip-tooltip': {
                                        backgroundColor: '#333',
                                        color: '#fff',
                                        fontSize: '0.75rem',
                                        maxWidth: 250,
                                        textAlign: 'center'
                                    }
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        height: 200,
                                        minHeight: 200,
                                        backgroundColor: 'rgba(30, 30, 30, 0.7) !important',
                                        backdropFilter: 'blur(5px)',
                                        borderRadius: 2,
                                        border: '2px solid',
                                        borderColor: 'divider',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'flex-start',
                                        textAlign: 'center',
                                        width: '100%',
                                        pt: 4
                                    }}
                                >
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 'medium' }}>
                                        Date Range
                                    </Typography>

                                    {dateRange && (
                                        <CompactDateRangePicker
                                            startDate={dateRange.start}
                                            endDate={dateRange.end}
                                            onChange={handleDateRangeChange}
                                        />
                                    )}

                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, fontSize: '0.7rem' }}>
                                        Select date range for analytics
                                    </Typography>
                                </Paper>
                            </Tooltip>
                        </Grid>
                    </>
                ) : (
                    // Original quote KPIs
                    <>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Total Quotes"
                                value={kpis.totalQuotes}
                                icon={<ClipboardList size={24} />}
                                color="primary"
                                tooltip="Total number of quotes in the system across all statuses"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="New Leads"
                                value={kpis.newLeads}
                                icon={<UserPlus size={24} />}
                                color="info"
                                tooltip="Number of new leads that haven't been processed yet"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="On-Site Quotes"
                                value={kpis.onSite}
                                icon={<Home size={24} />}
                                color="warning"
                                tooltip="Number of quotes scheduled for on-site visits or consultations"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Quotes Sent"
                                value={kpis.quotesSent}
                                icon={<Send size={24} />}
                                color="secondary"
                                tooltip="Number of quotes that have been sent to customers"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Quotes Won"
                                value={kpis.quotesWon}
                                icon={<Award size={24} />}
                                color="success"
                                tooltip="Number of quotes that have been accepted by customers"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Jobs In Progress"
                                value={kpis.jobsInProgress}
                                icon={<Briefcase size={24} />}
                                color="info"
                                tooltip="Number of active jobs that are currently in progress"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Needs Attention"
                                value={kpis.overThreshold}
                                icon={<AlertTriangle size={24} />}
                                color="error"
                                subtitle="Exceeding thresholds"
                                tooltip="Number of quotes that have exceeded their expected timeframes and need immediate attention"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4} lg={3}>
                            <KpiCard
                                title="Avg. Time to Win"
                                value={`${kpis.avgTimeToWin} days`}
                                icon={<Calendar size={24} />}
                                color="success"
                                tooltip="Average number of days it takes from quote creation to winning the job"
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
};

export default QuoteKPIs;
